import type { ReactNode } from "react";

const xeniFeatures: { text: string; icon: ReactNode }[] = [
  {
    text: "AI agent processes cases end-to-end",
    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="10" rx="2" /><circle cx="12" cy="5" r="2" /><path d="M12 7v4" /></svg>,
  },
  {
    text: "Auto-fills UKVI forms from client documents",
    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>,
  },
  {
    text: "Cross-checks supporting docs for conflicts",
    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>,
  },
  {
    text: "Reduces 3-hour admin tasks to minutes",
    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>,
  },
];

const xeniStats = [
  { label: "Beta clients (UK)", value: "15" },
  { label: "International partner", value: "ProActive Legal (AU/NZ)" },
  { label: "Pipeline cases", value: "200+" },
  { label: "Full-service firms in pipeline", value: "3+" },
  { label: "Client referrals", value: "2" },
];

const robolabFeatures: { text: string; icon: ReactNode }[] = [
  {
    text: "Purpose-built AI platform for children",
    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M8 14s1.5 2 4 2 4-2 4-2" /><line x1="9" y1="9" x2="9.01" y2="9" /><line x1="15" y1="9" x2="15.01" y2="9" /></svg>,
  },
  {
    text: "4-layer safety: input → keyword → AI audit → output",
    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>,
  },
  {
    text: "Student, parent, and school admin portals",
    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>,
  },
  {
    text: "UK/EU data residency, AES-256 encryption",
    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M12 4v16" /><path d="M2 12h20" /></svg>,
  },
];

const robolabStats = [
  { label: "Partnership type", value: "Exclusive" },
  { label: "Classes in session", value: "2" },
  { label: "March pilot students", value: "20" },
  { label: "Build time (MVP)", value: "1 month" },
  { label: "Expansion path", value: "GCSE/A-level" },
];

function ProductCard({
  title,
  statusLabel,
  statusBadge,
  features,
  stats,
}: {
  title: string;
  statusLabel: string;
  statusBadge: string;
  features: { text: string; icon: ReactNode }[];
  stats: { label: string; value: string }[];
}) {
  return (
    <div className="flex flex-col">
      <div className="flex justify-between items-center mb-3 pb-2 border-b border-[var(--color-border)]">
        <h3 className="text-[12px] font-[family-name:var(--font-mono)] font-bold text-[var(--color-fg)] uppercase tracking-[0.1em]">
          {title}
        </h3>
        <span className={`text-[9px] font-[family-name:var(--font-mono)] font-bold uppercase tracking-[0.1em] px-2 py-0.5 rounded-full ${statusBadge}`}>
          {statusLabel}
        </span>
      </div>
      <div className="flex flex-col gap-2 mb-4">
        {features.map((f) => (
          <div key={f.text} className="flex items-center gap-2">
            <span className="text-[var(--color-fg-tertiary)] shrink-0 w-4 h-4 flex items-center justify-center [&>svg]:w-full [&>svg]:h-full">{f.icon}</span>
            <span className="text-[12px] text-[var(--color-fg-secondary)] leading-tight">{f.text}</span>
          </div>
        ))}
      </div>
      <div className="pt-3 border-t border-[var(--color-border)] flex flex-col gap-1.5 font-[family-name:var(--font-mono)] text-[12px]">
        {stats.map((s, i) => (
          <div
            key={s.label}
            className="flex justify-between pb-1.5 text-[var(--color-fg-tertiary)]"
            style={{ borderBottom: i < stats.length - 1 ? "1px solid var(--color-border)" : "none" }}
          >
            <span>{s.label}</span>
            <span className="font-bold text-[var(--color-fg)]">{s.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ProofSlide({ isActive: _ }: { isActive: boolean }) {
  return (
    <div className="flex flex-col h-full">
      <div className="mb-5 max-w-3xl">
        <h2 className="text-[32px] font-light tracking-tight mb-0 font-[family-name:var(--font-heading)]">
          We&apos;ve already proven agents work<br />in regulated environments
        </h2>
      </div>

      <div className="grid grid-cols-2 gap-8 mb-8">
        <ProductCard
          title="XENI — PARALEGAL AGENT"
          statusLabel="LIVE"
          statusBadge="bg-emerald-500/20 text-emerald-600"
          features={xeniFeatures}
          stats={xeniStats}
        />
        <ProductCard
          title="ROBOLAB X — EDUCATION AGENT"
          statusLabel="LAUNCHING"
          statusBadge="bg-orange-500/10 text-orange-500"
          features={robolabFeatures}
          stats={robolabStats}
        />
      </div>
    </div>
  );
}
